import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Settings,
  Save,
  School,
  Mail,
  Phone,
  MapPin,
  Globe,
  Users,
  AlertTriangle,
  BookOpen,
  CheckCircle
} from 'lucide-react';
import { API_URL } from '@/services/apiClient';
import { safeLog } from '@/utils/safeLog';

function useSchoolAdminDarkMode() {
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('schoolAdminDarkMode') === 'true');
  useEffect(() => {
    const handleStorageChange = () => {
      setDarkMode(localStorage.getItem('schoolAdminDarkMode') === 'true');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  return darkMode;
}

interface SchoolData {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  country: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  domain: string | null;
  timezone: string | null;
  subscriptionTier: string | null;
  isActive: boolean;
  _count: {
    users: number;
    alerts: number;
    classrooms: number;
  };
}

export function SchoolSettings() {
  const dm = useSchoolAdminDarkMode();
  const [school, setSchool] = useState<SchoolData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    contactEmail: '',
    contactPhone: '',
    timezone: 'America/New_York'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/dashboard/school-admin/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSchool(data.school);
        setFormData({
          name: data.school.name || '',
          address: data.school.address || '',
          city: data.school.city || '',
          state: data.school.state || '',
          zipCode: data.school.zipCode || '',
          contactEmail: data.school.contactEmail || '',
          contactPhone: data.school.contactPhone || '',
          timezone: data.school.timezone || 'America/New_York'
        });
      }
    } catch (err) {
      safeLog.error('Failed to fetch settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setSaving(true);

    try {
      const token = sessionStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/dashboard/school-admin/settings`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess(true);
        fetchSettings();
        setTimeout(() => setSuccess(false), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save settings');
      }
    } catch (_err) {
      setError('Failed to connect to server');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${dm ? 'border-blue-400' : 'border-gray-900'}`}></div>
      </div>
    );
  }

  const cardCls = `rounded-xl border ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const sectionDivider = `border-t ${dm ? 'border-slate-700' : 'border-slate-100'}`;
  const titleCls = dm ? 'text-white' : 'text-slate-800';
  const descCls = dm ? 'text-white' : 'text-slate-500';
  const bodyTextCls = dm ? 'text-white' : 'text-slate-700';
  const mutedCls = dm ? 'text-white' : 'text-slate-400';
  const inputBg = dm ? 'bg-slate-700 border-slate-600 text-white placeholder:text-white' : '';

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-dm={dm}>

      {/* Overview stat cards */}
      {school && (
        <div className="grid gap-4 md:grid-cols-4">
          {[
            { label: 'Total Users',   value: school._count.users,      icon: Users,         color: 'text-blue-500',   bg: 'bg-blue-50' },
            { label: 'Total Alerts',   value: school._count.alerts,     icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-50' },
            { label: 'Classrooms',     value: school._count.classrooms, icon: BookOpen,      color: 'text-emerald-500', bg: 'bg-emerald-50' },
            { label: 'Subscription',   value: school.subscriptionTier || 'Free', icon: Globe, color: 'text-violet-500', bg: 'bg-violet-50' },
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className={`${cardCls} p-4`}>
              <div className="flex items-center justify-between mb-2">
                <p className={`text-xs font-medium ${mutedCls}`}>{label}</p>
                <div className={`h-7 w-7 rounded-lg flex items-center justify-center ${bg}`}>
                  <Icon className={`h-3.5 w-3.5 ${color}`} />
                </div>
              </div>
              <p className={`text-2xl font-bold capitalize ${titleCls}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* School Information Form */}
      <div className={`${cardCls} p-6`}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className={`text-base font-semibold flex items-center gap-2 ${titleCls}`}>
              <School className={`h-5 w-5 ${mutedCls}`} />
              School Information
            </h3>
            <p className={`text-sm mt-0.5 ${descCls}`}>Update your school&apos;s profile and contact details</p>
          </div>
          {success && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 flex items-center gap-1.5">
              <CheckCircle className="h-3.5 w-3.5" />
              Settings saved
            </span>
          )}
        </div>

        {error && (
          <div className={`mb-5 p-3 rounded-lg ${dm ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
            <p className={`text-sm ${dm ? 'text-red-400' : 'text-red-800'}`}>{error}</p>
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-6">
          <div className={sectionDivider} />

          <div>
            <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${mutedCls}`}>Contact Details</p>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="name" className={`text-xs font-medium ${bodyTextCls}`}>School Name</Label>
                <div className="relative">
                  <School className={`absolute left-3 top-3 h-4 w-4 ${mutedCls}`} />
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={`pl-10 ${inputBg}`} required />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="contactEmail" className={`text-xs font-medium ${bodyTextCls}`}>Contact Email</Label>
                <div className="relative">
                  <Mail className={`absolute left-3 top-3 h-4 w-4 ${mutedCls}`} />
                  <Input id="contactEmail" type="email" value={formData.contactEmail} onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })} className={`pl-10 ${inputBg}`} placeholder="admin@school.edu" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="contactPhone" className={`text-xs font-medium ${bodyTextCls}`}>Contact Phone</Label>
                <div className="relative">
                  <Phone className={`absolute left-3 top-3 h-4 w-4 ${mutedCls}`} />
                  <Input id="contactPhone" value={formData.contactPhone} onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })} className={`pl-10 ${inputBg}`} placeholder="(555) 123-4567" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="timezone" className={`text-xs font-medium ${bodyTextCls}`}>Timezone</Label>
                <Select value={formData.timezone} onValueChange={(v) => setFormData({ ...formData, timezone: v })}>
                  <SelectTrigger className={`btn-glass btn-glass-pill ${dm ? 'text-slate-200' : ''}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={dm ? 'bg-slate-800 border-slate-700 text-slate-200' : ''}>
                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                    <SelectItem value="America/Chicago">Central Time</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                    <SelectItem value="America/Anchorage">Alaska Time</SelectItem>
                    <SelectItem value="Pacific/Honolulu">Hawaii Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className={sectionDivider} />

          <div>
            <p className={`text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${mutedCls}`}>
              <MapPin className="h-3.5 w-3.5" />
              Address
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5 md:col-span-2">
                <Label htmlFor="address" className={`text-xs font-medium ${bodyTextCls}`}>Street Address</Label>
                <Input id="address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className={inputBg} placeholder="123 School Street" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="city" className={`text-xs font-medium ${bodyTextCls}`}>City</Label>
                <Input id="city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className={inputBg} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="state" className={`text-xs font-medium ${bodyTextCls}`}>State</Label>
                  <Input id="state" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} className={inputBg} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="zipCode" className={`text-xs font-medium ${bodyTextCls}`}>ZIP Code</Label>
                  <Input id="zipCode" value={formData.zipCode} onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })} className={inputBg} />
                </div>
              </div>
            </div>
          </div>

          <div className={sectionDivider} />

          <div className="flex justify-end">
            <button type="submit" disabled={saving} className="btn-glass btn-glass-pill btn-glass-blue flex items-center gap-2">
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* School Status */}
      {school && (
        <div className={`${cardCls} p-6`}>
          <h3 className={`text-base font-semibold flex items-center gap-2 mb-4 ${titleCls}`}>
            <Settings className={`h-5 w-5 ${mutedCls}`} />
            School Status
          </h3>
          <div className="grid gap-4 md:grid-cols-3">
            <div className={`p-4 rounded-xl border ${dm ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-100'}`}>
              <p className={`text-xs font-medium mb-1 ${mutedCls}`}>Status</p>
              <p className={`text-sm font-semibold flex items-center gap-2 ${bodyTextCls}`}>
                <span className={`w-2 h-2 rounded-full ${school.isActive ? 'bg-emerald-500' : 'bg-red-500'}`} />
                {school.isActive ? 'Active' : 'Inactive'}
              </p>
            </div>
            <div className={`p-4 rounded-xl border ${dm ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-100'}`}>
              <p className={`text-xs font-medium mb-1 ${mutedCls}`}>Domain</p>
              <p className={`text-sm font-semibold ${bodyTextCls}`}>{school.domain || 'Not set'}</p>
            </div>
            <div className={`p-4 rounded-xl border ${dm ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-100'}`}>
              <p className={`text-xs font-medium mb-1 ${mutedCls}`}>Country</p>
              <p className={`text-sm font-semibold ${bodyTextCls}`}>{school.country || 'USA'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
